// dashboard/auth/ClinicalRoleAttestationScreen.js
// Legal-risk audit (2026-09-10), finding #1: "Doctor" was a self-assignable
// app permission with no recorded claim that the holder is actually a
// licensed veterinarian. Blocks every clinical screen the first time a
// profile holds an active is_doctor=true membership, until they personally
// attest -- App.js renders this in place of the dashboard whenever
// membership.is_doctor is true and membership.clinical_attestation_at is
// not yet set (see the render gate right after the LoginScreen check).
// Deliberately separate from clinic signup itself, which stays gate-free
// per Bryan's explicit call (project_clinic_selfserve_no_gate).

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { supabase } from '../../supabase';
import { c, CAIRN_GLYPH_PATHS } from '../constants';
import { CLINICAL_ATTESTATION_VERSION } from '../../legalDocumentVersions';

export default function ClinicalRoleAttestationScreen({ onAttested, onLogout }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!checked || saving) return;
    setSaving(true); setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('attest_clinical_role', {
        p_version: CLINICAL_ATTESTATION_VERSION,
      });
      if (rpcErr) throw rpcErr;
      if (!data?.attested) {
        // 'not_applicable' (membership changed since load) or 'no_profile'
        // -- either way a stale gate, safest recovery is a clean reload.
        window.location.reload();
        return;
      }
      onAttested();
    } catch (err) {
      setError(err.message || t('clinicalAttestation.error'));
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 128 128">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slate, letterSpacing: 2 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, marginBottom: 4 }}>{t('clinicalAttestation.title')}</h1>
        <p style={{ fontSize: 13, color: c.textSoft, marginBottom: 20, lineHeight: 1.5 }}>{t('clinicalAttestation.subtitle')}</p>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: c.slate, lineHeight: 1.5, marginBottom: 20, padding: 14, backgroundColor: c.fog, borderRadius: 10 }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{ marginTop: 2 }}
            aria-label={t('clinicalAttestation.statement')}
          />
          <span>{t('clinicalAttestation.statement')}</span>
        </label>

        {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={!checked || saving}
          style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!checked || saving) ? 0.5 : 1, marginBottom: 10 }}
        >
          {saving ? t('common.saving') : t('clinicalAttestation.confirm')}
        </button>
        <button
          onClick={onLogout}
          disabled={saving}
          style={{ width: '100%', padding: '12px', backgroundColor: c.white, color: c.textSoft, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
        >
          {t('common.signOut')}
        </button>
      </div>
    </div>
  );
}
